// ============================================================
// PROPOSALS - what a move does to the ladder, as a SUGGESTION.
//
// Nothing here moves the ladder. After a quiet move is rolled, the app
// proposes a step and the GM accepts it, changes it, or leaves the ladder
// where it is. The table below is deliberately small and regular so a GM
// can predict it:
//
//   - a move that works leaves the ladder alone - except Hide, which lets
//     a wary scene settle one rung (never out of Alarm);
//   - a move that fails by a degree or two costs one rung;
//   - a move that fails by three or more costs two.
//
// An attack is its own case (`afterAttack`): a loud weapon brings the
// place to Searching at least; a quiet one that fails to land costs a
// rung; and when nobody who had noticed anything is left standing, the
// scene may settle back to Unaware.
//
// THE TABLE IS A RECONSTRUCTION of the one agreed in the brainstorm, whose
// exact figures were not written into the plan. It is data, and every
// proposal is the GM's to overrule - see dev-docs/decisions-log.md.
//
// Pure. Zero React dependency.
// ============================================================
import { RUNG_LABELS, hasNoticed, stepRung } from './ladder';
import type { Rung, StealthState } from './ladder';
import type { Noise, Signature } from './noise';

export const QUIET_MOVES = ['moveUnseen', 'hide', 'observe', 'distract', 'shadow', 'approach'] as const;
export type QuietMove = (typeof QUIET_MOVES)[number];

interface Steps {
  /** The move worked. */
  pass: number;
  /** It failed by one or two degrees. */
  fail: number;
  /** It failed by three or more. */
  badFail: number;
}

/** Degrees of failure from which a failure is a bad one. */
const BAD_FAIL_FROM = 3;

export const STEP_TABLE: Record<QuietMove, Steps> = {
  moveUnseen: { pass: 0, fail: 1, badFail: 2 },
  hide: { pass: -1, fail: 1, badFail: 1 },
  observe: { pass: 0, fail: 1, badFail: 1 },
  distract: { pass: 0, fail: 1, badFail: 2 },
  shadow: { pass: 0, fail: 1, badFail: 2 },
  approach: { pass: 0, fail: 1, badFail: 2 },
};

export interface Outcome {
  passed: boolean;
  /** Degrees between the two sides (or of the test itself): how clearly it went, always 0 or more. */
  margin: number;
}

export interface Proposal {
  /** Where the ladder would stand. Equal to the current rung when nothing is proposed. */
  rung: Rung;
  /** Why, in a sentence the GM reads beside the buttons. */
  reason: string;
}

const stays = (state: StealthState, reason: string): Proposal => ({ rung: state.rung, reason });

function moved(state: StealthState, by: number, why: string): Proposal {
  // Nothing settles out of Alarm by itself: once seen, it takes the GM's word.
  const rung = by < 0 && state.rung === 'alarm' ? state.rung : stepRung(state.rung, by);
  if (rung === state.rung) return stays(state, `${why} The ladder stays at ${RUNG_LABELS[rung]}.`);
  return { rung, reason: `${why} Proposed: ${RUNG_LABELS[rung]}.` };
}

/** The step a quiet move's outcome proposes. Total: every move, every outcome, every rung. */
export function proposeFor(move: QuietMove, outcome: Outcome, state: StealthState): Proposal {
  const steps = STEP_TABLE[move];
  if (outcome.passed) return moved(state, steps.pass, 'It worked.');
  const bad = outcome.margin >= BAD_FAIL_FROM;
  return moved(state, bad ? steps.badFail : steps.fail, bad ? 'It failed badly.' : 'It failed.');
}

export interface AttackOutcome {
  noise: Noise;
  /** The weapon's own step, when the heard word does not say it (plan armoury). */
  step?: Signature;
  /** Did the attack land? */
  hit: boolean;
  /** Ids of the opposition still standing AFTER it. */
  standing: readonly string[];
}

/** Is anybody who had noticed something still on their feet? */
const someoneStillLooking = (state: StealthState, standing: readonly string[]) =>
  standing.some((id) => hasNoticed(state, id));

/** A loud weapon: everyone heard it. Searching at least; an alarm already raised stays raised. */
function afterLoud(state: StealthState, step: Signature = 'loud'): Proposal {
  const rung: Rung = state.rung === 'alarm' ? 'alarm' : 'searching';
  const louder = RUNG_LABELS[rung];
  if (rung === state.rung) return stays(state, `A ${step} weapon. The ladder is already at ${louder}.`);
  // A muffled weapon was heard only by whoever stood near enough; a loud or deafening one by everyone.
  const who = step === 'muffled' ? 'it was heard close by' : 'everyone heard it';
  return { rung, reason: `A ${step} weapon: ${who}. Proposed: ${louder}.` };
}

/** A quiet weapon that landed: the ladder stays - or settles, when nobody who had noticed is left standing. */
function afterQuietHit(state: StealthState, standing: readonly string[]): Proposal {
  const wary = state.rung === 'uneasy' || state.rung === 'searching';
  if (wary && !someoneStillLooking(state, standing)) {
    return { rung: 'unaware', reason: 'Nobody who had noticed anything is left standing. Proposed: Unaware.' };
  }
  return stays(state, `Quiet, and it landed. The ladder stays at ${RUNG_LABELS[state.rung]}.`);
}

/** The step an attack made in Stealth proposes. */
export function afterAttack(outcome: AttackOutcome, state: StealthState): Proposal {
  if (outcome.noise === 'loud') return afterLoud(state, outcome.step);
  if (!outcome.hit) return moved(state, 1, 'A quiet weapon, but it did not land.');
  return afterQuietHit(state, outcome.standing);
}
